import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { jobsApi } from "@/lib/admin-api";
import { isAbortError, useBoundedRequest } from "@/hooks/use-bounded-request";
import {
  PENDING_TIMEOUT_MS,
  clearSettled,
  markPending,
  type PendingRuns,
} from "@/lib/pending-runs";
import type { RecurringJob } from "@/lib/jobs-types";

export const POLL_INTERVAL_MS = 3000;
/**
 * Hard deadline on any single request this hook makes. The shared api-client sets no fetch timeout of its
 * own, so without this a request that never *settles* never reaches its caller's `finally` and every
 * piece of state that `finally` owns is stranded. `useBoundedRequest` aborts at this bound, which makes
 * the promise reject and the `finally` run — no generation counters, no elapsed-time heuristics, and no
 * dependence on the pending deadline firing in the same millisecond. [code review r3]
 */
export const STALE_REQUEST_MS = POLL_INTERVAL_MS * 10;

export interface RecurringJobs {
  data: RecurringJob[];
  isLoading: boolean;
  /** A fetch has succeeded at least once, so an absent id means "not registered", not "not loaded yet". */
  loaded: boolean;
  triggering: ReadonlySet<string>;
  pending: PendingRuns;
  /** Silent fetch: no skeleton and no toast. The caller's poll tick. */
  refresh: () => Promise<void>;
  /** Run now / the Trigger job dialog: one pending lifecycle behind every surface. */
  triggerJob: (recurringJobId: string) => Promise<boolean>;
}

/**
 * The recurring-job definitions and their trigger lifecycle (sc-100769), shared by the Recurring Jobs grid and
 * the per-job details page (sc-114201). It loads on mount; polling stays with each page, because each polls on
 * its own predicate over these rows.
 */
export function useRecurringJobs(): RecurringJobs {
  const [data, setData] = useState<RecurringJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [triggering, setTriggering] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState<PendingRuns>(() => new Map());

  // Guards the ACTUAL request, so load(), refresh(), the poll tick and the post-trigger refetch can
  // never have two recurring GETs outstanding. usePollWhile's own overlap guard only sees the ticks it
  // starts, so it cannot cover the fire-and-forget refresh triggerJob issues, nor the manual refresh
  // button colliding with a tick.
  const inFlightRef = useRef(false);
  const { run } = useBoundedRequest(STALE_REQUEST_MS);
  // Latest fetched rows, read synchronously by triggerJob to capture the row's pre-trigger run id.
  const dataRef = useRef<RecurringJob[]>([]);

  const fetchRecurring = useCallback(
    async (visible: boolean) => {
      // Plain single-flight. It needs no staleness heuristic of its own: every request is bounded by
      // STALE_REQUEST_MS, so a hung one aborts, rejects, and reaches the `finally` below — which is what
      // releases this guard. That also covers the case no external timer could reach: a row that is
      // non-terminal on its own polls while arming no pending deadline at all.
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      if (visible) setIsLoading(true);

      let rows: RecurringJob[] = [];
      try {
        rows = await run((signal) => jobsApi.recurring(signal));
        dataRef.current = rows;
        setData(rows);
        setLoaded(true);
      } catch (err) {
        // A timed-out request is not a server failure and must not toast; the visible fetch reports the
        // rest, because a failing 3-second poll must not spam.
        if (visible && !isAbortError(err)) {
          toast.error(err instanceof Error ? err.message : "Failed to load recurring jobs");
        }
      } finally {
        inFlightRef.current = false;
        if (visible) setIsLoading(false);
        // Age pending entries on EVERY settled fetch, not just successful ones: `[]` clears nothing by
        // id but still expires entries by elapsed time, so an endpoint that keeps failing after a
        // trigger cannot leave the badge — and therefore the poll — alive forever.
        setPending((prev) => clearSettled(prev, rows, Date.now(), PENDING_TIMEOUT_MS));
      }
    },
    [run]
  );

  /** Visible fetch: skeleton + error toast. Mount. */
  const load = useCallback(() => fetchRecurring(true), [fetchRecurring]);
  /** Silent fetch: no skeleton (DataTableWrapper would strobe every tick) and no toast. */
  const refresh = useCallback(() => fetchRecurring(false), [fetchRecurring]);

  useEffect(() => {
    void load();
  }, [load]);

  // One handler behind every surface (a row's Run now, the Trigger job dialog, the details page's Run now), so
  // they share one pending lifecycle and the row is marked from the CLICK rather than from the 202.
  const triggerJob = useCallback(
    async (recurringJobId: string): Promise<boolean> => {
      // Snapshot the baseline BEFORE the POST. dataRef is rewritten by any fetch that settles while the
      // trigger is in flight, so reading it afterwards can capture the NEW run id (the row then stays
      // falsely pending until the escape hatch) or, on a dialog submit before the first fetch lands,
      // capture nothing and let that fetch's PRE-trigger id look like a change (the overlay clears early
      // and the previous terminal state reappears).
      const snapshot = dataRef.current.find((job) => job.id === recurringJobId);
      const baseline = {
        lastJobId: snapshot?.last_job_id ?? null,
        // An absent row means the last fetch failed or has not landed yet: `null` is UNKNOWN here, not
        // "never run", so a real id appearing later must not be read as settlement.
        observed: snapshot !== undefined,
      };

      setTriggering((prev) => new Set(prev).add(recurringJobId));
      try {
        // Bounded like the GETs. `markPending` runs only after a 202 — deliberately, so a 409 can never
        // leave a row pending — which means a POST that never settles arms no deadline of its own: the
        // `finally` below would be unreachable, leaving the Last state cell Pending, Run now disabled,
        // and the dialog stuck `submitting` with both its buttons disabled. Aborting makes the promise
        // reject, so every one of those owners runs its normal cleanup. [code review r3]
        await run((signal) => jobsApi.trigger(recurringJobId, signal));
        setPending((prev) => markPending(prev, recurringJobId, baseline, Date.now()));
        toast.success(`Triggered "${recurringJobId}"`);
        // Deliberately NOT awaited: heartbeat-class jobs finish in milliseconds, so an awaited refresh
        // usually returns the NEW run id and clearSettled would drop the overlay before the caller
        // resumes. Polling does not depend on it either — markPending already made shouldPoll true.
        void refresh();
        return true;
      } catch (err) {
        // A rejected trigger marks nothing, so a 409 can never leave a row pending or start a poll.
        // A deadline is not a server failure, and the server may well have accepted the trigger just
        // before the client gave up — telling the operator it failed invites a duplicate attempt.
        if (!isAbortError(err)) {
          toast.error(err instanceof Error ? err.message : "Failed to trigger job");
        }
        return false;
      } finally {
        setTriggering((prev) => {
          const next = new Set(prev);
          next.delete(recurringJobId);
          return next;
        });
      }
    },
    [refresh, run]
  );

  // The escape hatch still needs its own clock: `last_job_id` may never move (a job that never runs, or
  // a fetch that keeps failing), and no request deadline can bound that. It bounds the OVERLAY only.
  useEffect(() => {
    if (pending.size === 0) return;

    let earliest = Number.POSITIVE_INFINITY;
    for (const entry of pending.values()) earliest = Math.min(earliest, entry.startedAt);
    const delay = Math.max(0, earliest + PENDING_TIMEOUT_MS - Date.now());

    const id = window.setTimeout(() => {
      // Pending expiry, and nothing else. Earlier rounds also made this timer release the fetch guard,
      // clear the skeleton and fire a replacement — three jobs it was never in a position to do
      // correctly, because it could only act on whatever request happened to be outstanding at 30s and
      // relied on STALE_REQUEST_MS and PENDING_TIMEOUT_MS elapsing in the same millisecond even though
      // the two clocks start at different moments. Each request now carries its own deadline, so its own
      // `finally` does all of that. [code review r3]
      setPending((prev) => clearSettled(prev, [], Date.now(), PENDING_TIMEOUT_MS));
    }, delay);

    return () => {
      window.clearTimeout(id);
    };
  }, [pending]);

  return { data, isLoading, loaded, triggering, pending, refresh, triggerJob };
}
